"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence, useMotionValue, useTransform, animate } from "framer-motion";
import { XCircleIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { Button } from "@/components/ui/Button";
import { useUserLocale } from "@/hooks/useUserLocale";

interface PostImageOverlayProps {
    imageUrl: string;
    alt?: string;
    isOpen: boolean;
    onClose: () => void;
}

export function PostImageOverlay({ imageUrl, alt = "Image", isOpen, onClose }: PostImageOverlayProps) {
    const { t } = useUserLocale();
    const [scale, setScale] = useState(1);
    const [isDragging, setIsDragging] = useState(false);
    const x = useMotionValue(0);
    const y = useMotionValue(0);

    const scaleRef = useRef(1);

    // Reset position and scale when opening
    useEffect(() => {
        if (isOpen) {
            setScale(1);
            x.set(0);
            y.set(0);
            scaleRef.current = 1;
        }
    }, [isOpen, x, y]);

    useEffect(() => {
        scaleRef.current = scale;
    }, [scale]);

    // Smooth reset function for position and scale
    const resetImagePosition = useCallback(() => {
        // Animate position back to center
        animate(x, 0, {
            type: "spring",
            stiffness: 400,
            damping: 40,
            mass: 1
        });
        animate(y, 0, {
            type: "spring",
            stiffness: 400,
            damping: 40,
            mass: 1
        });
        // Reset scale
        setScale(1);
    }, [x, y]);

    // Handle pinch-to-zoom for touch devices
    const handlePinchZoom = useCallback((event: React.TouchEvent<HTMLImageElement>) => {
        if (event.touches?.length === 2) {
            // Calculate distance between two touches
            const touch1 = event.touches[0];
            const touch2 = event.touches[1];
            const distance = Math.sqrt(
                Math.pow(touch2.clientX - touch1.clientX, 2) + 
                Math.pow(touch2.clientY - touch1.clientY, 2)
            );
            
            // Store initial distance for comparison
            const target = event.target as any;
            if (!target._initialPinchDistance) {
                target._initialPinchDistance = distance;
                target._initialScale = scale;
            }
            
            const scaleChange = distance / target._initialPinchDistance;
            const newScale = Math.min(Math.max(target._initialScale * scaleChange, 0.5), 4);
            setScale(newScale);
        }
    }, [scale]);

    // Handle double tap to zoom
    const handleDoubleTap = useCallback(() => {
        const newScale = scale > 1 ? 1 : 2;
        setScale(newScale);
        if (newScale === 1) {
            // Use smooth animation to reset position
            animate(x, 0, {
                type: "spring",
                stiffness: 400,
                damping: 40,
                mass: 1
            });
            animate(y, 0, {
                type: "spring",
                stiffness: 400,
                damping: 40,
                mass: 1
            });
        }
    }, [scale, x, y]);    

    // Handle mouse wheel zoom (desktop)
    // const handleWheel = useCallback((event: WheelEvent) => {
    //     event.preventDefault();
    //     const delta = event.deltaY > 0 ? -0.1 : 0.1;
    //     const newScale = Math.min(Math.max(scale + delta, 0.5), 4);
    //     setScale(newScale);
    // }, [scale]);

    // Add wheel listener when overlay is open
    useEffect(() => {
        // Handle mouse wheel zoom (desktop)
        const handleWheel = (event: WheelEvent) => {
            event.preventDefault();
            const delta = event.deltaY > 0 ? -0.1 : 0.1;
            const newScale = Math.min(Math.max(scaleRef.current + delta, 0.5), 4);
            if (newScale >= 1 && newScale <= 4) {
                setScale(newScale);
            }
        };

        if (isOpen) {
            document.addEventListener('wheel', handleWheel, { passive: false });
            return () => document.removeEventListener('wheel', handleWheel);
        }
    }, [isOpen]);

    // Handle escape key
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && isOpen) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleKeyDown);
            return () => document.removeEventListener('keydown', handleKeyDown);
        }
    }, [isOpen, onClose]);

    // Prevent body scroll when overlay is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
            return () => {
                document.body.style.overflow = 'unset';
            };
        }
    }, [isOpen]);

    const handleBackdropClick = useCallback((event: React.MouseEvent) => {
        if (event.target === event.currentTarget) {
            onClose();
        }
    }, [onClose]);

    const handleDragStart = useCallback(() => {
        setIsDragging(true);
    }, []);

    const handleDragEnd = useCallback(() => {
        setIsDragging(false);
        // Reset position if scale is 1 (no zoom)
        if (scale <= 1) {
            animate(x, 0, {
                type: "spring",
                stiffness: 400,
                damping: 40,
                mass: 1
            });
            animate(y, 0, {
                type: "spring",
                stiffness: 400,
                damping: 40,
                mass: 1
            });
        }
    }, [scale, x, y]);    

    // Calculate drag constraints based on scale
    const getDragConstraints = useCallback(() => {
        if (scale <= 1) return { left: 0, right: 0, top: 0, bottom: 0 };
        
        const constraintFactor = (scale - 1) * 150; // Adjust based on image size
        return {
            left: -constraintFactor,
            right: constraintFactor,
            top: -constraintFactor,
            bottom: constraintFactor
        };
    }, [scale]);

    let tapCount = 0;
    const handleTap = useCallback(() => {
        tapCount++;
        setTimeout(() => {
            if (tapCount === 1) {
                // Single tap - do nothing or close if desired
            } else if (tapCount === 2) {
                handleDoubleTap();
            }
            tapCount = 0;
        }, 300);
    }, [handleDoubleTap]);

    return (
        <>
            {isOpen && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center"
                >
                    {/* Backdrop */}
                    <div
                        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
                        onClick={handleBackdropClick}
                    />

                    {/* Close button */}
                    <Button
                        isIconOnly
                        variant="light"
                        className="absolute top-4 right-4 z-10 bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white border-white/20"
                        onPress={onClose}
                    >
                        <XMarkIcon className="w-6 h-6" />
                    </Button>

                    {/* Image container */}
                    <div className="relative max-w-[100vw] max-h-[90vh] overflow-hidden">
                        <motion.img
                            src={imageUrl}
                            alt={alt}
                            className="max-w-full max-h-full object-contain select-none cursor-grab active:cursor-grabbing"
                            drag={scale > 1}
                            dragConstraints={getDragConstraints()}
                            dragElastic={0.1}
                            onDragStart={handleDragStart}
                            onDragEnd={handleDragEnd}
                            onTouchMove={handlePinchZoom}
                            onTouchEnd={(e) => {
                                // Clear pinch distance when touch ends
                                const target = e.currentTarget as any;
                                if (target._initialPinchDistance) {
                                    delete target._initialPinchDistance;
                                    delete target._initialScale;
                                }
                            }}
                            onClick={handleTap}
                            draggable={false}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ 
                                type: "spring", 
                                stiffness: 300, 
                                damping: 30,
                                mass: 0.8 
                            }}
                            style={{
                                scale: `${scale*100}%`,
                                x,
                                y
                            }}
                        />
                    </div>

                    {/* Bottom controls */}
                    <motion.div
                        className="absolute bottom-6 -translate-x-1/2 bg-black/40 backdrop-blur-sm rounded-full px-4 py-2"
                        initial={{ y: 20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: 20, opacity: 0 }}
                        transition={{ delay: 0.1 }}
                    >
                        <div className="flex items-center gap-3">
                            <Button
                                size="sm"
                                variant="light"
                                className="text-white hover:bg-white/20 min-w-16"
                                onPress={() => {
                                    const newScale = Math.max(scale - 0.5, 0.5);
                                    setScale(newScale);
                                    if (newScale <= 1) {
                                        animate(x, 0, {
                                            type: "spring",
                                            stiffness: 400,
                                            damping: 40,
                                            mass: 1
                                        });
                                        animate(y, 0, {
                                            type: "spring",
                                            stiffness: 400,
                                            damping: 40,
                                            mass: 1
                                        });
                                    }
                                }}
                                isDisabled={scale <= 1}
                            >
                                −
                            </Button>
                            
                            <span className="text-white text-sm font-medium min-w-12 text-center">
                                {Math.round(scale * 100)}%
                            </span>
                            
                            <Button
                                size="sm"
                                variant="light"
                                className="text-white hover:bg-white/20 min-w-16"
                                onPress={() => setScale(Math.min(scale + 0.5, 4))}
                                isDisabled={scale >= 4}
                            >
                                +
                            </Button>
                            
                            <div className="w-px h-6 bg-white/30 mx-1" />
                            
                            <Button
                                size="sm"
                                variant="light"
                                className="text-white hover:bg-white/20"
                                onPress={resetImagePosition}
                            >
                                <XCircleIcon className="w-6 h-6" />
                            </Button>
                        </div>
                    </motion.div>

                    {/* Instructions */}
                    <motion.div
                        className="absolute top-16 left-6 text-white/70 text-sm"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ delay: 0.3 }}
                    >
                        <div className="md:hidden">
                            {t.imageOverlay.zoomTip}
                        </div>
                    </motion.div>
                </div>
            )}
        </>
    );
}

export default PostImageOverlay;